import { EmptyState, Table } from '@dixa/lib-shared-habla';
import { UserPresence } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';

import { presenceApi, WorkloadLimits } from 'apis/presence';
import { TableFailedRow } from 'components/shared/tableFailedRow';
import { TableLoadingRow } from 'components/shared/tableLoadingRow';
import type {
  TeamListDispatchProps,
  TeamListStateProps,
} from 'components/teamPage/containers/teamList';
import { WithPresenceInjectedProps } from 'hocs';

import { TEAM_NUMBER_OF_COLUMNS } from '../teamPage';
import { TeamPageTableRow } from '../teamTableRow';

export type TeamListProps = WithPresenceInjectedProps &
  TeamListDispatchProps &
  TeamListStateProps;

export const TeamList = (props: TeamListProps): JSX.Element => {
  const { workloadLimits } =
    presenceApi.useReadOrganizationWorkloadSettingsQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        workloadLimits:
          data?.data?.load_limits ??
          ({
            email: 1,
            chat: 1,
            voicemail: 1,
            messaging: 1,
          } satisfies WorkloadLimits),
      }),
    });

  const listPresence = (userPresence: UserPresence): JSX.Element => {
    const {
      appPresence,
      agentsDetail,
      profile,
      favouriteTeamMemberIds,
      transferToQueueModalOpen,
      toggleAgentDetails,
    } = props;
    const agentId = userPresence.agent_id;
    const isOwnAgent = profile.id === agentId;
    const isFavorite = favouriteTeamMemberIds.includes(agentId);
    const toggleFavorite = isFavorite
      ? () => {
          props.personalRemoveFromFavouriteTeamMemberIds({ agentId });
          ampli.teamFavoriteToggled({ value: false });
        }
      : () => {
          props.personalAddToFavouriteTeamMemberIds({ agentId });
          ampli.teamFavoriteToggled({ value: true });
        };

    return (
      <TeamPageTableRow
        key={agentId}
        isFavorite={isFavorite}
        showUserModal={props.showUserModal}
        userPresence={userPresence}
        isOwnAgent={isOwnAgent}
        profileId={profile.id}
        appPresence={appPresence}
        limits={workloadLimits}
        isMicrophoneBlocked={props.isMicrophoneBlocked}
        twilioMakeInternalCall={props.twilioMakeInternalCall}
        isOpen={!!agentsDetail[agentId]}
        onFavoriteClick={toggleFavorite}
        toggleAgentDetails={toggleAgentDetails}
        transferToQueueModalOpen={transferToQueueModalOpen}
        getPresence={props.getPresence}
      />
    );
  };

  const renderLoadingStatus = (): JSX.Element => {
    const { presence } = props;

    if (presence.itemsAsyncStatus === 'failed') {
      return <TableFailedRow colSpan={TEAM_NUMBER_OF_COLUMNS} />;
    }

    return <TableLoadingRow colSpan={TEAM_NUMBER_OF_COLUMNS} />;
  };

  const { presence } = props;

  if (presence.itemsAsyncStatus !== 'succeeded') {
    return renderLoadingStatus();
  }

  return presence.items.length > 0 ? (
    <>{presence.items.map(listPresence)}</>
  ) : (
    <Table.Tr>
      <Table.Td colSpan={TEAM_NUMBER_OF_COLUMNS}>
        <EmptyState
          title="No agents found"
          description="Try to change some filters, or try searching for another name."
        />
      </Table.Td>
    </Table.Tr>
  );
};
